'use client';

import { Icon } from '@/general/Icons/Icon';
import { FC, useEffect, useEffectEvent, useState } from 'react';
import Cookies from 'universal-cookie';

import {
  COOKIE_EXPIRY_NRMA_PERKS_DISMISSED,
  COOKIE_NRMA_PERKS_DISMISSED,
  COOKIE_NRMA_TOKEN,
} from '@/constants/cookie.constants';
import { BasicModal } from '@/general/components/BasicModal/BasicModal';
import { Button } from '@/general/components/Button/Button';
import { Image } from '@repo/ui/Image';
import { PLP_BASE_PATH } from '@/plp/helpers/getPlpSlug';

const cookies = new Cookies();

type NrmaState = {
  hasNrmaToken: boolean;
  initialModalOpen: boolean;
};

function setPerksSeenCookie(): void {
  cookies.set(COOKIE_NRMA_PERKS_DISMISSED, '1', {
    path: '/',
    maxAge: Math.floor(COOKIE_EXPIRY_NRMA_PERKS_DISMISSED / 1000),
    sameSite: 'lax',
  });
}

const NRMA_MODAL_ID = 'plp-nrma-rewards-perks';

const BENEFITS: { icon: string; text: string }[] = [
  {
    icon: '/images/components/BadgeIllustration/nrma-7days-return.svg',
    text: "5 additional days of Carma's money back guarantee",
  },
  {
    icon: '/images/components/BadgeIllustration/nrma-thirdparty.svg',
    text: '5% off financing introducer fees',
  },
  {
    icon: '/images/components/BadgeIllustration/extended-coverage-warranty.svg',
    text: 'Bonus 3 months of Carma Essential Coverage OR\n 5% off the price of Carma Extended Coverage ',
  },
];

type Props = {
  /** When set, skips reading cookies (e.g. unit tests). */
  nrmaStateOverride?: NrmaState;
  onHide?: () => void;
  onModalClose?: () => void;
};

export const PlpNrmaTrigger: FC<Props> = ({ nrmaStateOverride, onHide, onModalClose }) => {
  const [nrmaState, setNrmaState] = useState<NrmaState | null>(null);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  const [isOpen, setIsOpen] = useState(false);

  const syncNrmaFromCookiesOrOverride = useEffectEvent(() => {
    if (nrmaStateOverride) {
      setIsOpen(nrmaStateOverride.initialModalOpen);
      return;
    }
    const token = cookies.get(COOKIE_NRMA_TOKEN);
    const hasNrmaToken = !!token;
    const hasPerksDismissed = !!cookies.get(COOKIE_NRMA_PERKS_DISMISSED);
    const next: NrmaState = {
      hasNrmaToken,
      initialModalOpen: hasNrmaToken && !hasPerksDismissed,
    };
    setNrmaState(next);
    setIsOpen(next.initialModalOpen);
  });

  useEffect(() => {
    syncNrmaFromCookiesOrOverride();
  }, [nrmaStateOverride, nrmaStateOverride?.hasNrmaToken, nrmaStateOverride?.initialModalOpen]);

  const effectiveState = nrmaStateOverride ?? nrmaState;
  const hasNrmaToken = effectiveState?.hasNrmaToken ?? false;

  const applyPerksSeenCookieWhenNeeded = useEffectEvent(() => {
    const effective = nrmaStateOverride ?? nrmaState;
    if (!effective) return;
    if (effective.initialModalOpen && isOpen) {
      setPerksSeenCookie();
    }
  });

  useEffect(() => {
    applyPerksSeenCookieWhenNeeded();
  }, [
    nrmaStateOverride?.hasNrmaToken,
    nrmaStateOverride?.initialModalOpen,
    nrmaState?.hasNrmaToken,
    nrmaState?.initialModalOpen,
    isOpen,
  ]);

  const openModal = () => {
    setIsOpen(true);
    setPerksSeenCookie();
  };
  const closeModal = () => {
    setIsOpen(false);
    onModalClose?.();
  };

  const onHideTrigger = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setBannerDismissed(true);
    onHide?.();
  };

  if (!effectiveState || !hasNrmaToken || bannerDismissed) return null;
  return (
    <div className="pb-4 md:border-t md:border-bor-neu-mut md:pt-4">
      <div className="relative w-full">
        <button
          type="button"
          onClick={openModal}
          className="c-fs-caption2 w-full cursor-pointer rounded-xs border-0 bg-[#2559F6] py-2 text-whi-0 underline transition-opacity hover:opacity-90"
          data-testid="plp-nrma-trigger"
        >
          View the My NRMA Rewards exclusive perks
        </button>
        <button
          type="button"
          onClick={onHideTrigger}
          className="absolute top-1/2 right-2 z-10 flex -translate-y-1/2 cursor-pointer items-center justify-center text-whi-0 md:right-6"
          aria-label="Dismiss"
          data-testid="plp-nrma-trigger-dismiss"
        >
          <Icon name="close" size={20} />
        </button>
      </div>

      <BasicModal
        id={NRMA_MODAL_ID}
        isOpen={isOpen}
        onClose={closeModal}
        className="[&_.BasicModal-footer]:px-5! [&_.BasicModal-footer]:py-2! [&_.BasicModal-headerTop]:pr-6! [&_.modalContentMain]:px-5! [&_.modalHeader]:px-5!"
        title={
          <div className="flex flex-col gap-1">
            <span className="c-fs-overline font-extrabold text-[#0A00F0]">MY NRMA REWARDS PERKS</span>
            <h3 className="c-fs-h5 font-extrabold text-tex-def-tie1">Welcome to Carma</h3>
            <p className="c-fs-body2 font-normal text-tex-def-tie2">
              As a NRMA Rewards member gain access to exclusive perks.
            </p>
          </div>
        }
        footer={
          <Button
            href={PLP_BASE_PATH}
            variant="accent"
            size="small"
            fullWidth
            onClick={closeModal}
            data-testid="plp-nrma-start-browsing"
          >
            Start browsing cars
          </Button>
        }
      >
        <div className="flex flex-col">
          <div className="relative mb-9 w-full rounded-lg">
            <Image
              src="/images/components/Nrma/nrma-welcome.png"
              alt=""
              width={640}
              height={320}
              className="aspect-2/1 w-full rounded-xs object-cover"
            />
            <Image
              src="/images/components/Nrma/nrma-logo-welcome.svg"
              alt=""
              width={120}
              height={60}
              className="absolute -bottom-6 left-1/2 z-20 block -translate-x-1/2 object-contain"
            />
          </div>

          <ul className="flex flex-col gap-2 md:px-2">
            {BENEFITS.map(({ icon, text }, index) => (
              <li key={icon} className={`flex gap-2 ${index === BENEFITS.length - 1 ? 'items-start' : 'items-center'}`}>
                <Image src={icon} alt={text} width={32} height={32} className="shrink-0" />
                <span className="c-fs-caption whitespace-pre-line text-tex-def-tie2">{text}</span>
              </li>
            ))}
          </ul>
        </div>
      </BasicModal>
    </div>
  );
};
